import { type DealOpportunityLpViewResponseV2 } from "@vega-api/generated/types/dealOpportunityLpViewResponseV2";
import { DotVariant, Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { formatMoney } from "@vega-core/utils/formatMoney";

type TermsTabProps = {
  data?: DealOpportunityLpViewResponseV2;
};

export const TermsTab = ({ data }: TermsTabProps) => {
  const ticketSize = data?.dealOpportunity.ticketSize;
  const indicativeOrder = data?.dealOpportunity.indicationOfInterestAmount;
  const allocationAmount = data?.dealOpportunity.allocationAmount;
  const commitmentAmount = data?.dealOpportunity.commitmentAmount;

  const formatTicketSize = () => {
    if (!ticketSize) return "-";

    return `${formatMoney(ticketSize.minimum)} - ${formatMoney(ticketSize.maximum)}`;
  };

  const getAllocationStatus = () => {
    if (!data?.dealOpportunity.allocationAmount) return null;

    if (data.dealOpportunity.allocationAcceptedAt) {
      return {
        variant: TagVariants.Positive,
        label: m.coInvestments_termsAgreed_allocationStatus_accepted(),
        dotVariant: DotVariant.CHECK,
      };
    }

    if (data.isDealOpportunityRejected) {
      return {
        variant: TagVariants.Negative,
        label: m.coInvestments_termsAgreed_allocationStatus_declined(),
        dotVariant: DotVariant.REMOVED,
      };
    }

    return null;
  };

  const allocationStatus = getAllocationStatus();

  return (
    <div className="flex flex-col gap-6" data-testid="terms-tab">
      <div className="body-base-regular text-text/secondary mb-4">
        {m.coInvestments_termsAgreed_description()}
      </div>
      {/* Ticket Size */}
      <div
        className="border border-border/primary rounded-companyBorderRadius p-6 bg-background/card flex flex-col gap-4"
        data-testid="terms-tab-ticket-size-section"
      >
        <h3 className="body-large-semibold">
          {m.coInvestments_termsAgreed_ticketSize()}
        </h3>
        <div className="body-base-regular text-text/secondary">
          {m.coInvestments_termsAgreed_ticketSize_description()}
        </div>
        <div className="text-text/primary body-semi-large-medium">
          {formatTicketSize()}
        </div>
      </div>

      {/* Indicative Order */}
      <div
        className="border border-border/primary rounded-companyBorderRadius p-6 bg-background/card flex flex-col gap-4"
        data-testid="terms-tab-indicative-order-section"
      >
        <h3 className="body-large-semibold">
          {m.coInvestments_termsAgreed_indicativeOrder()}
        </h3>
        <div className="body-base-regular text-text/secondary">
          {m.coInvestments_termsAgreed_indicativeOrder_description()}
        </div>
        <div className="text-text/primary body-semi-large-medium">
          {indicativeOrder ? formatMoney(indicativeOrder) : "-"}
        </div>
      </div>

      {/* Allocation Amount */}
      <div
        className="border border-border/primary rounded-companyBorderRadius p-6 bg-background/card flex flex-col gap-4"
        data-testid="terms-tab-allocation-amount-section"
      >
        <h3 className="body-large-semibold">
          {m.coInvestments_termsAgreed_allocationAmount()}
        </h3>
        <div className="body-base-regular text-text/secondary">
          {m.coInvestments_termsAgreed_allocationAmount_description()}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text/primary body-semi-large-medium">
            {allocationAmount ? formatMoney(allocationAmount) : "-"}
          </span>
          {allocationStatus ? (
            <Tag
              dataTestId="allocation-status-tag"
              dotVariant={allocationStatus.dotVariant}
              variant={allocationStatus.variant}
            >
              {allocationStatus.label}
            </Tag>
          ) : null}
        </div>
      </div>

      {/* Commitment Amount */}
      <div
        className="border border-border/primary rounded-companyBorderRadius p-6 bg-background/card flex flex-col gap-4"
        data-testid="terms-tab-commitment-amount-section"
      >
        <h3 className="body-large-semibold">
          {m.coInvestments_termsAgreed_commitmentAmount()}
        </h3>
        <div className="body-base-regular text-text/secondary">
          {m.coInvestments_termsAgreed_commitmentAmount_description()}
        </div>
        <div className="text-text/primary body-semi-large-medium">
          {commitmentAmount ? formatMoney(commitmentAmount) : "-"}
        </div>
      </div>
    </div>
  );
};
